import { BorderRadius, Color, Spacing } from '@persona-id/design-tokens';
import { PlusIcon, XIcon } from '@persona-id/design-tokens/icons';
import { kebabCase } from 'lodash/fp';
import { useCallback, useEffect, useMemo, VFC } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import styled from 'styled-components';

import FieldSchemaSelect from 'dashboard/components/FieldSchemaSelect';
import ReactSelect from 'dashboard/components/ReactSelect';
import {
  generateJsonSchemaConfig,
  REPORT_OBJECT_KEYS,
  UsableObjectConfig,
  VERIFICATION_OBJECT_KEYS,
} from 'dashboard/lib/workflows';
import { WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import { useSchemaForFieldPath } from 'dashboard/redux/selectors/workflow-editor-context';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import { JsonSchemaCustomTypes } from 'lib/fields';
import { JsonSchemaTypes } from 'lib/json-schema';
import { useFormSecurely } from 'lib/security/reactHookForm';

const MAX_ARGUMENTS = 10;

// 'API Object' input exposes Persona object with external API representation
// Should allow only objects which has public API doc
const API_OBJECTS: WorkflowFieldObjectKey[] = [
  WorkflowFieldObjectKey.Account,
  WorkflowFieldObjectKey.Case,
  WorkflowFieldObjectKey.Inquiry,
  WorkflowFieldObjectKey.Transaction,
  WorkflowFieldObjectKey.DocumentGeneric,
  ...REPORT_OBJECT_KEYS,
  ...VERIFICATION_OBJECT_KEYS,
];

// adjusting height to align with design system's input field
const narrowReactSelectStyles = {
  indicatorsContainer: () => ({
    display: 'none',
  }),
  valueContainer: () => ({
    height: 'auto',
    minHeight: '30px',
    paddingTop: 0,
    paddingBottom: 0,
  }),
};

const Well = styled.div`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BorderRadius.Md};
  padding: ${Spacing[4]};
`;

const ArgKeyInput = styled(Input)`
  width: 144px;
`;

const ArgTypeSelectWrapper = styled.div`
  width: 132px;
  margin-left: ${Spacing[2]};
`;

const ArgFieldSelect = styled.div`
  width: 240px;
  margin-left: ${Spacing[2]};
`;

const ArgDeleteButton = styled(Button)`
  margin-left: ${Spacing[2]};
  // use variant='transparent' after it's out
  background-color: transparent;
  // For general browser, show DeleteButton per hovering Row but keep existence for a11y
  opacity: 0;
  &:hover,
  &:focus {
    opacity: 1;
  }
  &:focus {
    color: ${Color.CorePurple800};
  }
`;

const ArgRaw = styled.div`
  height: 32px;
  display: flex;
  align-items: center;

  & + & {
    margin-top: ${Spacing[2]};
  }

  &:hover > ${ArgDeleteButton} {
    opacity: 1;
  }
`;

const ButtonRaw = styled.div`
  margin-top: ${Spacing[3]};
`;

enum ArgType {
  Object = 'object',
  Field = 'field',
}

interface InternalFormData {
  argsList: { key: string; type: ArgType; value: string }[];
}

interface InputArgumentsProps {
  args: Record<string, string>;
  editable: boolean;
  usableObjects: UsableObjectConfig[];
  onChange: (newArgs: Record<string, string>) => void;
}

const InputArguments: VFC<InputArgumentsProps> = ({ args, editable, usableObjects, onChange }) => {
  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    setValue,
    trigger,
    watch,
  } = useFormSecurely<InternalFormData>({
    defaultValues: {
      argsList: Object.entries(args).map(([key, value]) => ({
        key,
        type: usableObjects.find((object) => object.value === value)
          ? ArgType.Object
          : ArgType.Field,
        value,
      })),
    },
    shouldFocusError: false,
  });

  const schemaForFieldPath = useSchemaForFieldPath();

  const { append, fields, remove } = useFieldArray({ control, name: 'argsList' });

  const watchedInput = watch('argsList');

  useEffect(() => {
    const subscription = watch(() => {
      handleSubmit((form) => {
        if (editable) {
          onChange(form.argsList.reduce((acc, arg) => ({ ...acc, [arg.key]: arg.value }), {}));
        }
      })();
    });
    return () => subscription.unsubscribe();
  }, [editable, handleSubmit, onChange, watch]);

  // Enable validation from the beginning
  useEffect(() => {
    trigger();
  }, [trigger]);

  const addNewProperty = () => {
    append({
      key: `property${getValues('argsList').length + 1}`,
      type: ArgType.Object,
      value: usableObjects[0].value,
    });
  };

  const jsonSchema = useMemo(
    () =>
      generateJsonSchemaConfig({
        usableObjects,
        useOriginal: true,
      }).jsonSchema,
    [usableObjects],
  );

  const isValidValue = useCallback(
    (path: string) => !!schemaForFieldPath(path, jsonSchema).schema,
    [jsonSchema],
  );

  return (
    <Well role='form'>
      {fields.length === 0 && <ArgRaw>No Properties</ArgRaw>}
      {fields.map((field, index) => (
        <ArgRaw key={field.id}>
          <Controller
            name={`argsList.${index}.key`}
            control={control}
            rules={{
              required: true,
              validate: (currentKey) =>
                getValues().argsList.filter(({ key }) => key === currentKey).length < 2,
            }}
            render={({ field }) => (
              <ArgKeyInput
                {...field}
                disabled={!editable}
                hasError={!!errors.argsList?.[index]?.key}
                maxLength={20}
                placeholder='key name'
                size='md'
                onChange={(e) => {
                  // Eventually, follows workflows API key's inflection setting.
                  // Its default is now kebab and customer can't change
                  field.onChange(kebabCase(e.target.value));
                }}
              />
            )}
          />
          <ArgTypeSelectWrapper>
            <Controller
              name={`argsList.${index}.type`}
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  aria-label={`Select field type of ${index + 1}`}
                  disabled={!editable}
                  portal={false}
                  onValueChange={(value) => {
                    setValue(
                      `argsList.${index}.value`,
                      value === ArgType.Object ? usableObjects[0].value : '',
                    );
                    field.onChange(value);
                  }}
                >
                  <Option value={ArgType.Object}>API Object</Option>
                  <Option value={ArgType.Field}>Field</Option>
                </Select>
              )}
            />
          </ArgTypeSelectWrapper>
          <ArgFieldSelect>
            <Controller
              control={control}
              name={`argsList.${index}.value`}
              rules={{ validate: isValidValue }}
              render={({ field: { value, onChange }, fieldState: { error } }) =>
                watchedInput[index].type === ArgType.Object ? (
                  <ReactSelect
                    aria-label={`Value for ${index + 1}`}
                    disabled={!editable}
                    styles={narrowReactSelectStyles}
                    hasError={!!error}
                    id={`input-arguments__object-options-${index}`}
                    options={usableObjects.filter((o) =>
                      API_OBJECTS.some((apiObject) => apiObject === o.workflowObjectKey),
                    )}
                    value={value}
                    onChange={onChange}
                  />
                ) : (
                  <FieldSchemaSelect
                    allowedTypes={[
                      JsonSchemaCustomTypes.Date,
                      JsonSchemaCustomTypes.Enum,
                      JsonSchemaTypes.Boolean,
                      JsonSchemaTypes.Integer,
                      JsonSchemaTypes.Number,
                      JsonSchemaTypes.String,
                    ]}
                    ariaLabel={`Value for ${index + 1}`}
                    disabled={!editable}
                    hasError={value !== '' && !!error}
                    id={`input-arguments__field-options-${index}`}
                    initialValue={value ?? ''}
                    size='sm'
                    usableObjects={usableObjects}
                    onFieldSelect={(selected) => {
                      onChange(selected.value);
                    }}
                  />
                )
              }
            />
          </ArgFieldSelect>
          {editable && (
            <ArgDeleteButton
              aria-label={`Delete ${index}`}
              id={`input-arguments__delete-field-${index}`}
              variant='inverse'
              width='icon'
              onClick={() => remove(index)}
            >
              <XIcon />
            </ArgDeleteButton>
          )}
        </ArgRaw>
      ))}
      {editable && watchedInput.length < MAX_ARGUMENTS && usableObjects.length > 0 && (
        <ButtonRaw>
          <Button
            id='input-arguments__add-new-field'
            prefixIcon={<PlusIcon />}
            type='button'
            variant='inverse'
            width='auto'
            onClick={addNewProperty}
          >
            Add property
          </Button>
        </ButtonRaw>
      )}
    </Well>
  );
};

export default InputArguments;
